import clsx from "clsx";
import React, { useMemo } from "react";
import { getTypographyClassName } from "@appsmith/wds-theming";
import { DateInput, DateSegment, Group } from "react-aria-components";

// Relative imports (not the "@appsmith/wds" barrel) keep this component out
// of the package-barrel dependency cycle counted by the CI cyclic-deps gate.
import type { SIZES } from "../../../shared";
import { textInputStyles } from "../../Input";
import { Spinner } from "../../Spinner";
import { IconButton } from "../../IconButton";

import styles from "./styles.module.css";

interface DateRangePickerTriggerProps {
  isLoading?: boolean;
  size?: Exclude<keyof typeof SIZES, "xSmall" | "large">;
  isDisabled?: boolean;
}

/**
 * Range trigger: renders TWO date-segment groups (start + end) inside a
 * single input group, with the same suffix affordances as DatepickerTrigger
 * (calendar icon button, or a spinner while isLoading — M3 async contract).
 */
export const DateRangePickerTrigger = (props: DateRangePickerTriggerProps) => {
  const { isDisabled, isLoading, size } = props;

  const suffix = useMemo(() => {
    if (Boolean(isLoading)) return <Spinner />;

    return (
      <IconButton
        color={Boolean(isLoading) ? "neutral" : "accent"}
        icon="calendar-month"
        isDisabled={isDisabled}
        isLoading={isLoading}
        size={size === "medium" ? "small" : "xSmall"}
        variant={Boolean(isLoading) ? "ghost" : "filled"}
      />
    );
  }, [isLoading, size, isDisabled]);

  return (
    <Group className={textInputStyles.inputGroup}>
      <div
        className={clsx(
          textInputStyles.input,
          styles.rangeInput,
          getTypographyClassName("body"),
        )}
        data-date-range-input
      >
        <DateInput className={styles.dateInput} slot="start">
          {(segment) => <DateSegment segment={segment} />}
        </DateInput>
        <span aria-hidden="true">–</span>
        <DateInput className={styles.dateInput} slot="end">
          {(segment) => <DateSegment segment={segment} />}
        </DateInput>
      </div>
      <span data-input-suffix>{suffix}</span>
    </Group>
  );
};
